import Vue from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { MESSAGE_TYPES } from '@/enums'

const initialState = () => ({
  lastMessageTimestamp: null,
  messages: [],
  fixedMessages: [],
  unreadMessages: 0,
  feedbackSid: null,
  feedbackStep: 0,
  homeSid: null,
  scrollAlways: false,
  scrollSmooth: false,
  scrollEnabled: true,
  scrollToLastSelector: null,
})

export const state = initialState()

export const getters = {
  getMessageBySid: (state) => (sid) => {
    // Search first on converation list
    let message = state.messages.find((item) => item.sid === sid)
    // If no exist try on fixed message
    if (!message) {
      message = state.fixedMessages.find((item) => item.sid === sid)
    }
    return message
  },
  /**
   * Should or not display MessageTile component
   */
  isVisible: (state, getters, rootState, rootGetters) => (message) => {
    if (message.attributes?.hidden) {
      return false
    } else if (message.type === MESSAGE_TYPES.FEEDBACK) {
      // Only last Dydu message feedback should be visible
      return (
        message.sid === getters.getLastMessage?.sid &&
        rootGetters['Widget/isBotSession']
      )
    }
    return true
  },
  getLastMessage: (state) => state.messages[state.messages.length - 1],
  getLastVisibleMessage: (state) =>
    state.messages
      .filter(
        (message) =>
          !Boolean(message.attributes?.hidden) &&
          message.type !== MESSAGE_TYPES.FEEDBACK,
      )
      .slice(-1)[0],
  getLastMessageByType: (state) => (type) =>
    state.messages.filter((message) => message.type === type).slice(-1)[0],
}

export const mutations = {
  RESET(state, { history = false }) {
    const newState = initialState()
    Object.keys(newState).forEach((key) => {
      if (history && key === 'messages') {
        state.messages = filterSatisfactionAndClosingMessages(state.messages)
        return
      }

      state[key] = newState[key]
    })
  },
  /**
   * @param {Object} state
   * @param {Date} payload
   */
  UPDATE_LAST_MESSAGE_TIMESTAMP(state, payload) {
    state.lastMessageTimestamp = payload
  },
  UPDATE_MESSAGES_CHAT(state, payload) {
    const set = new Set()

    state.messages = [...state.messages, ...payload].reduce((acc, item) => {
      if (!set.has(item.sid)) {
        set.add(item.sid)
        acc.push(item)
      } else if (item.type === 'media') {
        const mediaIndex = acc.findIndex((accItem) => accItem.sid === item.sid)
        acc[mediaIndex] = item
      }
      return acc
    }, [])
  },
  UPDATE_MESSAGES_BOT(state, payload) {
    const set = new Set()

    state.messages = [...state.messages, ...payload].reduce((acc, item) => {
      if (!set.has(item.sid)) {
        set.add(item.sid)
        acc.push(item)
      }
      return acc
    }, [])
  },
  UPDATE_MESSAGES_VISIBILITY(state, { sid, visible = true }) {
    const indexMessage = state.messages.findIndex(
      (message) => message.sid === sid,
    )
    if (~indexMessage) {
      Vue.set(state.messages[indexMessage].attributes, 'hidden', !visible)
    }
  },
  UPDATE_MESSAGES_STEP_ACTIONS(state, { sid, step }) {
    // Find sid from conversation messages list
    const indexMessage = state.messages.findIndex(
      (message) => message.sid === sid,
    )
    // If it doesn't exit try on fixedmessage
    const indexFixedMessage = ~indexMessage
      ? -1
      : state.fixedMessages.findIndex((message) => message.sid === sid)

    if (~indexMessage) {
      Vue.set(
        state.messages[indexMessage].attributes.stepActions,
        'currentStep',
        step,
      )
    } else if (~indexFixedMessage) {
      Vue.set(
        state.fixedMessages[indexFixedMessage].attributes.stepActions,
        'currentStep',
        step,
      )
    }
  },
  SET_FIXED_MESSAGES(state, payload) {
    const set = new Set()

    state.fixedMessages = [...payload].reduce((acc, item) => {
      if (!set.has(item.sid)) {
        set.add(item.sid)
        acc.push(item)
      }
      return acc
    }, [])
  },
  REMOVE_FIXED_MESSAGES(state) {
    state.fixedMessages = []
  },
  INCREASE_UNREAD_MESSAGES(state) {
    ++state.unreadMessages
  },
  RESET_UNREAD_MESSAGES(state) {
    state.unreadMessages = 0
  },
  UPDATE_FEEDBACK_SID(state, sid) {
    state.feedbackSid = sid
  },
  UPDATE_FEEDBACK_STEP(state, step) {
    state.feedbackStep = step
  },
  UPDATE_HOME_SID(state, sid) {
    state.homeSid = sid
  },
  UPDATE_SCROLL_ALWAYS(state, payload = false) {
    state.scrollAlways = payload
  },
  UPDATE_SCROLL_SMOOTH(state, payload = false) {
    state.scrollSmooth = payload
  },
  UPDATE_SCROLL_ENABLED(state, payload = false) {
    state.scrollEnabled = payload
  },
  UPDATE_SCROLL_TO_LAST_SELECTOR(state, selector) {
    state.scrollToLastSelector = selector
  },
  SET_MESSAGE_SATISFACTION_COMPLETED(state, sid) {
    const message = state.messages.find((item) => item.sid === sid)

    if (!message || message?.type !== MESSAGE_TYPES.SATISFACTION) {
      return
    }

    Vue.set(message.attributes, 'surveyCompletedTimestamp', new Date())
  },
  DELETE_MESSAGES(state) {
    state.messages = []
  },
  REMOVE_MESSAGE_BY_SID(state, payload) {
    state.messages = state.messages.filter((item) => item.sid !== payload)
  },
}

export const actions = {
  /**
   * Reset state
   * @param {*} { commit }
   */
  reset({ commit, rootGetters }) {
    commit('RESET', {
      history: rootGetters['Widget/displayChatHistoryConversation'],
    })
  },
  updateOtherMessage({ state, commit }, { target, type, messages }) {
    if (state.scrollAlways) {
      commit('UPDATE_SCROLL_ALWAYS', true)
    }
    let newMessages = Array.isArray(messages) ? messages : [messages]

    newMessages = newMessages.map((message) => {
      return {
        body: message,
        timestamp: new Date(),
        sid: uuidv4(),
        attributes: {
          type,
          incoming: false,
        },
      }
    })

    commit(`UPDATE_MESSAGES_${target}`, newMessages)
  },
  /**
   *
   *
   * @param {*} { state, commit }
   * @param {String} target = SESSION_TYPES.BOT || SESSION_TYPES.CHAT
   * @param {String} sessionType = MESSAGE_TYPES.LIVE_CHAT || MESSAGE_TYPES.BOT
   * @param {String} message
   */
  updateWelcomeMessage({ state, commit }, { target, sessionType, message }) {
    if (state.scrollAlways) {
      commit('UPDATE_SCROLL_ALWAYS', true)
    }
    const newMessages = [
      {
        body: message,
        timestamp: new Date(),
        sid: uuidv4(),
        type: MESSAGE_TYPES.WELCOME,
        attributes: {
          type: sessionType,
          incoming: false,
        },
      },
    ]

    commit(`UPDATE_MESSAGES_${target}`, newMessages)
  },
  pushSatisfactionMessage({ commit, rootState }, { target, type }) {
    const newMessages = [
      {
        timestamp: new Date(),
        sid: uuidv4(),
        type: MESSAGE_TYPES.SATISFACTION,
        attributes: {
          type,
          incoming: false,
          channelSid: rootState.LiveChat.channelSid,
        },
      },
    ]

    commit(
      'UPDATE_SCROLL_TO_LAST_SELECTOR',
      `[data-message-id="${newMessages[0].sid}"]`,
    )
    commit('UPDATE_SCROLL_ALWAYS', true)

    commit(`UPDATE_MESSAGES_${target}`, newMessages)
  },
  pushTimestampMessage({ commit }, { target }) {
    const newMessages = [
      {
        timestamp: new Date(),
        sid: uuidv4(),
        attributes: {
          type: MESSAGE_TYPES.TIMESTAMP,
          incoming: false,
        },
      },
    ]

    commit(`UPDATE_MESSAGES_${target}`, newMessages)
  },
  /**
   * Reset feedback state to display new form at the end of the conversation
   *
   * @param {String} [payload.sid] : message sid
   */
  resetFeedback({ commit }, sid) {
    commit('UPDATE_FEEDBACK_SID', sid)
    commit('UPDATE_FEEDBACK_STEP', 1)
  },
  changeMessageStep({ commit, getters }, { sid, step }) {
    commit('UPDATE_SCROLL_ALWAYS', false)
    commit('UPDATE_MESSAGES_STEP_ACTIONS', { sid, step })
    const message = getters.getMessageBySid(sid)
    if (message) {
      commit('Sidebar/UPDATE_SIDEBAR_ID', sid, { root: true })
    }
  },
  initialScrollSetup({ commit }) {
    commit('UPDATE_SCROLL_ENABLED', true)
    commit('UPDATE_SCROLL_ALWAYS', true)
    commit('UPDATE_SCROLL_SMOOTH', false)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}

/**
 *
 * Extracted Logic
 *
 */

function notCompletedSatisfactionMessage(message) {
  return (
    message.type === MESSAGE_TYPES.SATISFACTION &&
    !message.attributes.surveyCompletedTimestamp
  )
}

function closingMessage(message) {
  return message.attributes.type === MESSAGE_TYPES.CLOSING
}

/**
 * Only preserve messages which are not satisfaction or close one
 *
 * @param {*} messages : state messages
 * @returns {Array} : messages list
 */
function filterSatisfactionAndClosingMessages(messages) {
  return messages.filter((message) => {
    return !notCompletedSatisfactionMessage(message) && !closingMessage(message)
  })
}
